import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Search, MapPin, ArrowRight, CheckCircle, Store, Utensils } from 'lucide-react';
import { retailStores, horecaOutlets, retailerBrands, horecaBrands } from '../utils/mockData';
import { pushEvent } from '../utils/analytics';
import usePageMeta from '../hooks/usePageMeta';

const API_BASE = '';

const useQueryParam = (key) => {
  const location = useLocation();
  return useMemo(() => {
    const p = new URLSearchParams(location.search);
    return p.get(key) || '';
  }, [location.search, key]);
};

const FindUs = () => {
  usePageMeta({
    title: 'Find Us | No-Belly Bier at Stores, Bars & Restaurants',
    description: 'Locate No-Belly Bier at premium liquor stores and 50+ HORECA partners across Gurgaon, Faridabad, Panchkula & Goa. Search by city, area or outlet.',
    path: '/find-us',
  });

  const initialCity = useQueryParam('city');
  const [tab, setTab] = useState('retail'); // retail | horeca
  const [query, setQuery] = useState('');

  const source = tab === 'retail' ? retailStores : horecaOutlets;

  // Derive city chips from the ACTIVE dataset so we never show empty chips
  const cities = useMemo(() => {
    const unique = Array.from(new Set(source.map(s => s.city))).sort();
    return ['All', ...unique];
  }, [source]);

  const [city, setCity] = useState('All');

  // Sync URL ?city= into state on first load and whenever it changes.
  // Do NOT include `city` in deps — otherwise a user click gets reverted back
  // to the URL param on the very next render.
  useEffect(() => {
    if (initialCity && cities.includes(initialCity)) setCity(initialCity);
  }, [initialCity, cities]);

  // If the active tab no longer offers the current city, gracefully fall back
  useEffect(() => {
    if (!cities.includes(city)) setCity('All');
  }, [cities, city]);

  const [form, setForm] = useState({ name: '', phone: '', city: '', message: '', consent: false, website: '' });
  const [state, setState] = useState({ loading: false, success: false, error: '' });

  // Paginate long outlet lists — initial 15, Load More adds 15 at a time.
  const PAGE_SIZE = 15;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [tab, city, query]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return source.filter(s => {
      const cityOk = city === 'All' || s.city === city;
      const qOk = !q || `${s.name} ${s.area || ''} ${s.city}`.toLowerCase().includes(q);
      return cityOk && qOk;
    });
  }, [source, city, query]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { setState({ loading: false, success: false, error: 'Please accept the privacy consent to continue.' }); return; }
    setState({ loading: true, success: false, error: '' });
    try {
      const res = await fetch(`${API_BASE}/api/find-us/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = body.detail;
        const msg = Array.isArray(d) ? d.map(x => x.msg || 'Invalid').join(' · ') : (d || 'Something went wrong.');
        throw new Error(msg);
      }
      setState({ loading: false, success: true, error: '' });
      // Note: 'find_us_inquiry_success' is not part of the required GA4
      // event list and is intentionally not tracked. The confirmed success
      // UI state below is the only visible feedback.
      setForm({ name: '', phone: '', city: '', message: '', consent: false, website: '' });
    } catch (err) {
      setState({ loading: false, success: false, error: err.message });
    }
  };

  const brands = useMemo(() => {
    const all = tab === 'retail' ? retailerBrands : horecaBrands;
    if (city === 'All') return all;
    // Retailer brands carry a `cities` list; HORECA brands don't (all Gurgaon
    // today) so we simply hide them when a non-Gurgaon city is picked.
    if (tab === 'retail') {
      return all.filter(b => Array.isArray(b.cities) && b.cities.includes(city));
    }
    return city === 'Gurgaon' ? all : [];
  }, [tab, city]);

  return (
    <main className="nb-page nb-page-find" data-testid="page-find-us">
      <section className="nb-page-hero">
        <div className="nb-page-hero-inner">
          <p className="nb-eyebrow">FIND US</p>
          <h1 className="nb-display-md">POUR YOURSELF ONE.</h1>
          <p className="nb-page-lede">
            No-Belly is stocked at premium liquor stores and 50+ bars, restaurants and hotels across
            Gurgaon, Faridabad, Panchkula &amp; Goa.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="nb-find-tabs" data-testid="find-tabs">
        <button
          type="button"
          onClick={() => setTab('retail')}
          className={`nb-find-tab ${tab === 'retail' ? 'active' : ''}`}
          data-testid="find-tab-retail"
        >
          <Store size={16} /> Liquor Stores
        </button>
        <button
          type="button"
          onClick={() => setTab('horeca')}
          className={`nb-find-tab ${tab === 'horeca' ? 'active' : ''}`}
          data-testid="find-tab-horeca"
        >
          <Utensils size={16} /> Bars · Restaurants · Hotels
        </button>
      </section>

      {/* Filters */}
      <section className="nb-find-filters">
        <div className="nb-find-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            aria-label="Search outlets"
            placeholder={tab === 'retail' ? 'Search outlet or area…' : 'Search venue…'}
            value={query}
            onChange={(e) => {
              const v = e.target.value;
              setQuery(v);
              const trimmed = v.trim();
              if (trimmed.length >= 3) pushEvent('store_search', { search_term: trimmed });
            }}
            data-testid="find-search"
          />
        </div>
        <div className="nb-find-cities" role="tablist" aria-label="Filter outlets by city">
          {cities.map(c => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={city === c}
              tabIndex={city === c ? 0 : -1}
              onClick={() => {
                setCity(c);
                if (c !== 'All') pushEvent('city_filter_selected', { city: c, button_location: 'find_us_city_chip' });
              }}
              className={`nb-city-chip ${city === c ? 'active' : ''}`}
              data-testid={`find-city-chip-${c.toLowerCase()}`}
            >{c}</button>
          ))}
        </div>
      </section>

      {/* Results count */}
      <p className="nb-find-count" data-testid="find-result-count">
        Showing <strong>{filtered.length}</strong> {tab === 'retail' ? 'stores' : 'venues'}
        {city !== 'All' ? ` in ${city}` : ''}
        {query ? ` matching "${query}"` : ''}
      </p>

      {/* Brand logo strip */}
      {brands && brands.length > 0 && (
        <section className="nb-find-brandstrip" data-testid="find-brand-strip">
          {brands.map(b => (
            <div key={b.id} className="nb-find-brand" data-testid={`find-brand-${b.slug}`}>
              <img src={b.logo} alt={b.name} loading="lazy" />
            </div>
          ))}
        </section>
      )}

      {/* Outlet list — hidden on HORECA tab: HORECA is shown as a logos-only strip above */}
      {tab === 'retail' && (
        <section className="nb-find-list" data-testid="find-list">
          {filtered.length === 0 ? (
            <p className="nb-microcopy" data-testid="find-empty">No outlets match your search. Try a different city or clear the search.</p>
          ) : (
            filtered.slice(0, visibleCount).map(item => (
              <article key={item.id} className="nb-outlet-card" data-testid={`outlet-${item.id}`}>
                <div className="nb-outlet-mark" aria-hidden="true"><MapPin size={16} /></div>
                <div className="nb-outlet-body">
                  <h3 className="nb-outlet-name">{item.name}</h3>
                  <p className="nb-outlet-meta">
                    <span>{item.city}</span>
                    {item.area ? <><span className="nb-dot" aria-hidden="true">·</span><span>{item.area}</span></> : null}
                  </p>
                </div>
              </article>
            ))
          )}
        </section>
      )}

      {tab === 'retail' && filtered.length > visibleCount && (
        <div className="nb-find-loadmore-wrap">
          <button
            type="button"
            className="nb-cta-ghost nb-cta-lg"
            onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
            data-testid="find-load-more"
          >
            LOAD MORE ({filtered.length - visibleCount} MORE)
          </button>
        </div>
      )}

      {/* Inquiry form */}
      <section className="nb-find-inquiry" data-testid="find-inquiry">
        <div className="nb-find-inquiry-copy">
          <p className="nb-eyebrow">CAN'T FIND US NEARBY?</p>
          <h2 className="nb-display-sm">ASK US WHERE TO BUY.</h2>
          <p className="nb-copy">
            Drop your details and our distribution team will point you to the nearest outlet — or add
            one to our route.
          </p>
        </div>
        <form className="nb-inquiry-form" onSubmit={submit} noValidate data-testid="find-inquiry-form">
          <input
            type="text" name="website" tabIndex="-1" autoComplete="off"
            value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
            style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', opacity: 0 }} aria-hidden="true"
          />
          {state.success ? (
            <div className="nb-form-success" data-testid="find-inquiry-success"><CheckCircle size={22} /> Thanks — we'll be in touch shortly.</div>
          ) : (
            <>
              <input type="text" placeholder="Full name" required minLength={2} maxLength={80}
                name="name" autoComplete="name" aria-label="Full name"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="find-inquiry-name" />
              <input type="tel" placeholder="Phone" required minLength={7} maxLength={25}
                name="phone" autoComplete="tel" aria-label="Phone number"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="find-inquiry-phone" />
              <input type="text" placeholder="City" required minLength={2} maxLength={60}
                name="city" autoComplete="address-level2" aria-label="City"
                value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="find-inquiry-city" />
              <textarea placeholder="Which area / outlet are you looking for? (optional)"
                maxLength={500} value={form.message} name="message" aria-label="Message"
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="find-inquiry-message" />
              <label className="nb-consent">
                <input type="checkbox" required checked={form.consent}
                  onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  data-testid="find-inquiry-consent" />
                <span>I am 21+ and agree to the <Link to="/privacy" className="nb-inline-link">Privacy Policy</Link>.</span>
              </label>
              {state.error && <p className="nb-form-error" role="alert" data-testid="find-inquiry-error">{state.error}</p>}
              <button type="submit" className="nb-cta-primary" disabled={state.loading} data-testid="find-inquiry-submit">
                {state.loading ? 'Sending…' : 'Send Inquiry'}{!state.loading && <ArrowRight size={16} />}
              </button>
            </>
          )}
        </form>
      </section>
    </main>
  );
};

export default FindUs;
